import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { AggregatedStatus, ModuleStatus, StatusItem } from '@evo-hub/shared';
import { api } from '../lib/api.js';
import { balancedColumns } from '../lib/balancedColumns.js';
import i18n from '../i18n.js';
import { Card, CardTitle } from '../components/ui/Card.js';
import { CounterBadge } from '../components/ui/CounterBadge.js';
import { Pill } from '../components/PillGroup.js';
import { Icon, type IconName } from '../components/Icon.js';
import { HomeClaudePanel } from '../components/dashboard/HomeClaudePanel.js';

function shortTimestamp(iso: string): string {
  const d = new Date(iso.endsWith('Z') ? iso : iso + 'Z');
  const day = d.getDate();
  const month = d.toLocaleString(i18n.language, { month: 'short' }).toLowerCase();
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${day} ${month}, ${hh}:${mm}`;
}

// ── Home — fuente única de estado (ver packages/shared/src/status/contract.ts) ─
// Consume GET /api/status. No arma su propio resumen: cada tarjeta muestra
// exactamente lo que el módulo devuelve — count total, hasta `cap` ítems,
// "ver todos". Un módulo en count=0 no monta tarjeta (evita panel de tarjetas
// vacías); si todos están en cero, se muestra una sola línea.

const MODULE_ICON: Record<string, IconName> = {
  messaging: 'messages',
  tasks: 'tasks',
  email: 'email',
  leads: 'leads',
  forms: 'forms',
  billing: 'billing',
  projects: 'projects',
  reports: 'reports',
};

// Ruta a la que apunta "ver todos" por módulo. `messaging` no tiene página
// propia — cada ítem ya linkea a su destino real (bitácora del contacto o
// task de triage), pero no existe una vista "todos los pendientes de
// mensajería" a la que mandar el link agregado. Se omite a propósito (ver
// StatusModuleCard) en vez de apuntar a algo que no es lo que dice ser.
const MODULE_VIEW_ALL: Partial<Record<string, string>> = {
  tasks: '/tasks',
  email: '/email?tab=followup',
  leads: '/leads',
  forms: '/formularios',
  billing: '/facturacion',
  projects: '/proyectos',
  reports: '/reports',
};

const DETAIL_TONE: Record<string, 'danger' | 'warn' | 'muted'> = {
  failed: 'danger',
  invoice_overdue: 'danger',
  window_closing: 'warn',
  needs_approval: 'muted',
  invoice_sent: 'muted',
  estimate_sent: 'muted',
};

function useAggregatedStatus() {
  return useQuery<AggregatedStatus>({
    queryKey: ['status-aggregated'],
    queryFn: () => api.get<AggregatedStatus>('/api/status'),
    refetchInterval: 60_000,
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

function StatusItemRow({ item }: { item: StatusItem }) {
  const { t } = useTranslation();
  const rawDate = item.detailKey === 'scheduled' && typeof item.detailParams?.['date'] === 'string'
    ? item.detailParams['date'] as string
    : null;
  return (
    <Link to={item.link} style={{ textDecoration: 'none', display: 'block' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--evo-space-2)',
          padding: 'var(--evo-space-4) 0 var(--evo-space-2)',
          borderBottom: '1px solid var(--evo-stroke)',
        }}
      >
        <span
          style={{
            fontSize: 'var(--evo-list-title-fs)',
            color: 'var(--evo-fg-1)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {item.label}
        </span>
        {item.detailKey && (
          <Pill size="xs" variant="subtle" tone={DETAIL_TONE[item.detailKey] ?? 'muted'}>
            {rawDate ? shortTimestamp(rawDate) : t(`home.status.detail.${item.detailKey}`, item.detailParams ?? {})}
          </Pill>
        )}
      </div>
    </Link>
  );
}

function StatusItemGroup({ label, items }: { label?: string; items: StatusItem[] }) {
  if (items.length === 0) return null;
  return (
    <div>
      {label && (
        <span style={{
          display: 'block', fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)',
          textTransform: 'uppercase', letterSpacing: 'var(--evo-ls-caps)', padding: 'var(--evo-space-2) 0 0',
        }}>
          {label}
        </span>
      )}
      {items.map(item => <StatusItemRow key={item.id} item={item} />)}
    </div>
  );
}

function StatusModuleCard({ status }: { status: ModuleStatus }) {
  const { t } = useTranslation();
  const viewAllTo = MODULE_VIEW_ALL[status.module];

  // Task Manager muestra dos grupos separados — esperando aprobación (exige
  // acción) y agendadas (informativo, ya va a correr sola). Ver moduleStatus/
  // tasks.ts: count solo cuenta el primer grupo.
  const isTasks = status.module === 'tasks';
  const pendingItems = isTasks ? status.items.filter(i => i.detailKey !== 'scheduled') : status.items;
  const scheduledItems = isTasks ? status.items.filter(i => i.detailKey === 'scheduled') : [];

  return (
    <Card
      padding="none"
      header={
        <>
          <Icon name={MODULE_ICON[status.module] ?? 'activity'} size={16} />
          <CardTitle>{t(`home.status.module.${status.module}`)}</CardTitle>
          <div style={{ flex: 1 }} />
          <CounterBadge count={status.count} />
        </>
      }
    >
      <div style={{ padding: '0 var(--evo-space-4)' }}>
        {isTasks ? (
          <>
            <StatusItemGroup label={t('home.status.tasksGroup.pending')} items={pendingItems} />
            <StatusItemGroup label={t('home.status.tasksGroup.scheduled')} items={scheduledItems} />
          </>
        ) : (
          status.items.map(item => <StatusItemRow key={item.id} item={item} />)
        )}
        {viewAllTo && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', padding: 'var(--evo-space-2) 0' }}>
            <Link
              to={viewAllTo}
              style={{ color: 'var(--evo-cyan)', fontSize: 'var(--evo-fs-body)', textDecoration: 'none' }}
            >
              {t('home.status.viewAll')}
            </Link>
          </div>
        )}
      </div>
    </Card>
  );
}

function StatusGrid({ modules }: { modules: ModuleStatus[] }) {
  // count>0 = exige acción. items.length>0 cubre tasks con solo agendadas
  // (count=0 a propósito — ver moduleStatus/tasks.ts) que igual deben mostrarse.
  const pending = modules.filter(m => m.count > 0 || m.items.length > 0);
  if (pending.length === 0) return null;
  const cols = balancedColumns(pending.length);
  return (
    <div
      className="evo-status-grid"
      /* el conteo va en una variable CSS: los topes por ancho hacen
         min(tope, activos) y así nunca aparece un carril vacío */
      style={{ '--status-cols': cols, gap: 'var(--evo-space-4)' } as React.CSSProperties}
    >
      {pending.map(m => <StatusModuleCard key={m.module} status={m} />)}
    </div>
  );
}

export function DashboardPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useAggregatedStatus();

  const modules = data?.modules ?? [];
  const accion = modules.filter(m => m.category === 'accion');
  const actividad = modules.filter(m => m.category === 'actividad');


  const isEmpty = (m: ModuleStatus) => m.count === 0 && m.items.length === 0;
  const allZero = !isLoading && modules.length > 0 && modules.every(isEmpty);

  return (
    // 2-column layout: contenido | claude — mismo patrón que ReportsPage
    // (list | detail | claude), acá sin panel de lista propio.
    <div style={{ height: '100%', display: 'flex', overflow: 'hidden' }}>
      <div
        className="animate-fade-blur-in"
        style={{
          flex: 1,
          minWidth: 0,
          overflowY: 'auto',
          padding: 'var(--evo-space-6) var(--evo-page-pad-x)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--evo-space-6)',
        }}
      >
        {isLoading && (
          <p style={{ fontSize: 'var(--evo-fs-body-lg)', color: 'var(--evo-fg-3)' }}>…</p>
        )}

        {allZero && (
          <p style={{ fontSize: 'var(--evo-fs-body-lg)', color: 'var(--evo-fg-3)', textAlign: 'center', margin: 'var(--evo-space-7) 0' }}>
            {t('home.status.allClear')}
          </p>
        )}

        {/* Una sola rejilla para TODO lo que exige acción. Antes había dos
            grupos (primario/secundario) con rejilla propia: con 1-2 módulos
            activos por grupo, cada tarjeta salía a ancho completo y el Home
            se apilaba en una columna aunque hubiera 3-4 activos en total. */}
        {!allZero && accion.some(m => !isEmpty(m)) && (
          <section style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-3)' }}>
            <CardTitle>{t('home.status.zone.accion')}</CardTitle>
            <StatusGrid modules={accion} />
          </section>
        )}

        {!allZero && actividad.some(m => !isEmpty(m)) && (
          <section style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-3)' }}>
            <CardTitle>{t('home.status.zone.actividad')}</CardTitle>
            <StatusGrid modules={actividad} />
          </section>
        )}
      </div>

      <HomeClaudePanel />
    </div>
  );
}
